/* Local display and project-cost entry; canonical package prices and checkout stay EUR. */
(() => {
  const preferenceKey = 'starsbooster:display-currency';
  const currencies = Object.freeze(['EUR', 'USD', 'GBP', 'CAD', 'AUD', 'CHF', 'JPY', 'CNY', 'INR', 'BRL', 'SGD', 'HKD', 'AED', 'SAR', 'TRY', 'PLN', 'SEK', 'NOK', 'NZD', 'KRW', 'MXN', 'ZAR']);
  const rates = new Map();
  let selected = 'EUR';
  let rate = null;
  let costQuote = null;
  let generation = 0;
  let manualChoice = false;
  const controls = () => [...document.querySelectorAll('[data-sb-display-currency]')];
  const parseRate = (data, expected = 'USD', now = Date.now()) => {
    const value = data?.rate;
    const date = data?.date;
    const timestamp = Date.parse(`${date}T00:00:00Z`);
    const age = now - timestamp;
    if (!currencies.includes(expected) || expected === 'EUR' || data?.quote !== expected || data?.base !== 'EUR'
      || typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)
      || !Number.isFinite(value) || value <= 0 || value > 1000000 || !Number.isFinite(age)
      || new Date(timestamp).toISOString().slice(0, 10) !== date
      || age < -86400000 || age > 7 * 86400000) throw new Error('Exchange rate unavailable');
    if (!Number.isSafeInteger(data.rateScaled) || data.rateScaled !== Math.round(value * 100000000)
      || typeof data.token !== 'string' || data.token.length > 1500 || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43}$/.test(data.token)
      || !Number.isSafeInteger(data.expiresAt) || data.expiresAt <= now) throw new Error('Exchange rate unavailable');
    return { value, date, quote: Object.freeze({ currency: expected, rateScaled: data.rateScaled, token: data.token, date, expiresAt: data.expiresAt }) };
  };
  const readPreference = () => {
    try {
      const value = localStorage.getItem(preferenceKey);
      return currencies.includes(value) ? value : null;
    } catch { return null; }
  };
  const message = (key, fallback) => document.querySelector('[data-sb-currency-status]')?.dataset[key] || fallback;
  const format = (minor, currency = 'EUR') => {
    const original = Number(minor || 0);
    const converted = selected !== 'EUR' && currency === 'EUR' && rate !== null;
    const target = converted ? selected : currency;
    const fractionDigits = new Intl.NumberFormat('en', { style: 'currency', currency: target }).resolvedOptions().maximumFractionDigits;
    const amount = converted ? original * rate / 100 : original / 100;
    const roundedMinor = Math.round(amount * 10 ** fractionDigits);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: target,
      minimumFractionDigits: roundedMinor % 10 ** fractionDigits ? fractionDigits : 0,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  };
  const refreshStatic = (scope = document) => {
    scope.querySelectorAll('[data-sb-eur-minor]').forEach((node) => {
      const amount = Number(node.dataset.sbEurMinor);
      if (!Number.isFinite(amount)) return;
      if (node.dataset.sbEurOriginal === undefined) node.dataset.sbEurOriginal = node.textContent;
      const text = selected !== 'EUR' ? format(amount, 'EUR') : node.dataset.sbEurOriginal;
      if (node.textContent !== text) node.textContent = text;
    });
    document.querySelectorAll('[data-sb-currency-note]').forEach((node) => {
      node.hidden = selected === 'EUR';
      const template = node.dataset.sbCurrencyTemplate || 'Approximate __CURRENCY__ display. Checkout and renewals are charged in EUR.';
      const text = template.replaceAll('__CURRENCY__', selected);
      if (node.textContent !== text) node.textContent = text;
    });
  };
  const select = async (requested, { automatic = false } = {}) => {
    if (automatic && (manualChoice || readPreference())) return;
    const value = currencies.includes(requested) ? requested : 'EUR';
    if (!automatic) {
      manualChoice = true;
      try { localStorage.setItem(preferenceKey, value); } catch { /* Optional display preference. */ }
    }
    const operation = ++generation;
    const status = document.querySelector('[data-sb-currency-status]');
    if (status) status.textContent = value !== 'EUR' ? message('sbCurrencyLoading', 'Loading exchange rate…') : '';
    try {
      let result = null;
      if (value !== 'EUR') {
        try { result = parseRate(rates.get(value), value); } catch { /* Fetch a fresh rate. */ }
      }
      if (value !== 'EUR' && !result) {
        const endpoint = `https://conversion.starsbooster.io/api/checkout/exchange-rate?currency=${value}`;
        const response = await fetch(endpoint, { credentials: 'omit', referrerPolicy: 'no-referrer', signal: AbortSignal.timeout(8000) });
        if (!response.ok) throw new Error('Exchange rate unavailable');
        const data = await response.json();
        result = parseRate(data, value);
        if (operation !== generation) return;
        rates.set(value, data);
      }
      if (operation !== generation) return;
      selected = value;
      rate = result?.value ?? null;
      costQuote = result?.quote ?? null;
      if (status) status.textContent = '';
    } catch {
      if (operation !== generation) return;
      selected = 'EUR';
      rate = null;
      costQuote = null;
      if (status) status.textContent = message('sbCurrencyUnavailable', 'A recent exchange rate is unavailable. Prices remain in EUR.');
    } finally {
      if (operation === generation) {
        controls().forEach((control) => { control.value = selected; control.disabled = false; });
        refreshStatic();
        document.dispatchEvent(new CustomEvent('sb:display-currency', { detail: { currency: selected } }));
      }
    }
  };
  window.SBDisplayMoney = Object.freeze({ format, parseRate, select, currencies, get currency() { return selected; }, get costQuote() { return costQuote; }, get hasPreference() { return manualChoice || readPreference() !== null; } });
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-sb-display-currency]')) select(event.target.value);
  });
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; refreshStatic(); });
  });
  observer.observe(document.body, { childList: true, subtree: true });
  refreshStatic();
  const saved = readPreference();
  if (saved) select(saved);
})();
